// Note blocks (MB — reclassified out of "blocked / M23"). PURE (ENG-1).
//
// M23 gates *audio output*, not the note block itself: the block-entity note
// value, the instrument selected by the block underneath, the redstone
// rising-edge / hand-hit trigger, and the pitch formula are all pure and testable
// today. This module owns that logic; the host stores the per-cell note and, once
// AUD-1 lands, routes `notePlayback().{instrument,pitch}` to the sound engine.
// Until then the host emits a silent play event carrying the same payload.

/** A note block cycles through 25 notes (F#3 … F#5, two octaves), 0..24. */
export const NOTE_COUNT = 25;

/**
 * The instrument a note block plays is chosen by the block directly beneath it.
 * A representative slice of Java's table — matched by block name first (the
 * special cases) then by material (wood → bass, stone → basedrum), else harp.
 */
export function instrumentFor(belowName: string | null, belowMaterial?: string): string {
  const n = belowName ?? '';
  // Exact / suffix name matches (the distinctive instruments).
  if (n === 'gold_block') return 'bell';
  if (n === 'clay') return 'flute';
  if (n === 'packed_ice' || n === 'blue_ice' || n === 'ice') return 'chime';
  if (n === 'bone_block') return 'xylophone';
  if (n === 'iron_block') return 'iron_xylophone';
  if (n === 'emerald_block') return 'bit';
  if (n === 'hay_block') return 'banjo';
  if (n === 'glowstone') return 'pling';
  if (n === 'pumpkin' || n === 'carved_pumpkin') return 'didgeridoo';
  if (n === 'soul_sand') return 'cow_bell';
  if (n.endsWith('_wool')) return 'guitar';
  if (n.endsWith('glass') || n.endsWith('_glass_pane') || n === 'sea_lantern' || n === 'beacon') return 'hat';
  if (n.endsWith('sand') || n === 'gravel' || n.endsWith('_concrete_powder')) return 'snare';
  if (n.endsWith('_planks') || n.endsWith('_log') || n.endsWith('_wood') || belowMaterial === 'wood') return 'bass';
  // Stone family → bass drum.
  if (
    belowMaterial === 'stone' || n.endsWith('stone') || n.endsWith('_bricks') ||
    n === 'netherrack' || n === 'basalt' || n.endsWith('quartz_block')
  ) return 'basedrum';
  return 'harp';
}

/** Right-click advances the note by one, wrapping 24 → 0. */
export function cycleNote(note: number): number {
  return (((note | 0) % NOTE_COUNT) + NOTE_COUNT + 1) % NOTE_COUNT;
}

/** Playback rate for a note: 2^((note − 12)/12) — 0.5 at note 0, 1.0 at 12, 2.0 at 24. */
export function pitchOf(note: number): number {
  const n = Math.max(0, Math.min(NOTE_COUNT - 1, note | 0));
  return 2 ** ((n - 12) / 12);
}

/** Particle hue 0..1 (Java tints the note particle by note/24). */
export function noteHue(note: number): number {
  const n = Math.max(0, Math.min(NOTE_COUNT - 1, note | 0));
  return n / (NOTE_COUNT - 1);
}

export interface NotePlayback {
  instrument: string;
  note: number;
  pitch: number;
  hue: number;
}

/** The full play-event payload for a note block over `belowName` at `note`. */
export function notePlayback(note: number, belowName: string | null, belowMaterial?: string): NotePlayback {
  const n = Math.max(0, Math.min(NOTE_COUNT - 1, note | 0));
  return { instrument: instrumentFor(belowName, belowMaterial), note: n, pitch: pitchOf(n), hue: noteHue(n) };
}
